import { Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CustomWorkflowService } from 'app/services/custom-workflow/custom-workflow.service';
import {
  WorkflowInstance,
  WorkflowInstanceState,
  WorkflowStep,
  WorkflowStepState,
  WorkflowTemplateStepType,
  WorkflowTemplateType,
} from 'app/types';
import { map, switchMap } from 'rxjs';
import { getUserIdentifier } from '../../../utils/people';

@Component({
  selector: 'side-panel-activity',
  templateUrl: './side-panel-activity.component.html',
  styleUrl: './side-panel-activity.component.scss',
})
export class SidePanelActivityComponent {
  protected readonly WorkflowInstanceState = WorkflowInstanceState;
  protected readonly WorkflowTemplateType = WorkflowTemplateType;
  protected readonly WorkflowTemplateStepType = WorkflowTemplateStepType;

  customWorkflowService = inject(CustomWorkflowService);

  $templates = input(0, { alias: 'templates' });
  $fileId = input<string | undefined>('', { alias: 'fileId' });
  $loading = input(false, { alias: 'loading' });
  $workflowInstances = input([], {
    alias: 'workflowInstances',
    transform: (instances: WorkflowInstance[] | undefined) =>
      (instances ?? []).sort(
        (a, b) =>
          new Date(b.finished_at ?? b.updated_at).getTime() -
          new Date(a.finished_at ?? a.updated_at).getTime(),
      ),
  });

  startWorkflowEvent = output<void>();
  approveWorkflowStepEvent = output<number>();
  approveWorkflowStepWithConfidentiality = output<void>();
  rejectWorkflowEvent = output<number>();
  acknowledgeWorkflowEvent = output<number>();
  drivePreview = output<string>();

  readonly DEFAULT_ICON_STATE = 'pi-question';

  $runningStep = computed(() =>
    this.$workflowInstances()?.reduce(
      (acc, w) =>
        acc ?? w.steps.find((s) => s.state === WorkflowStepState.IN_PROGRESS),
      null! as WorkflowStep,
    ),
  );

  private runningStep$ = toObservable(this.$runningStep);
  private authorizations$ = this.runningStep$.pipe(
    map((step) => step?.id),
    switchMap((id) => this.customWorkflowService.canContinueWorkflowStep(id)),
  );
  $authorizations = toSignal(this.authorizations$);

  approveWorkflow(instance: WorkflowInstance, step: WorkflowStep) {
    const validations = instance.steps.filter(
      (step) => step.template.type === 'user_validation',
    );

    if (
      validations.at(-1) === step &&
      step.state === WorkflowStepState.IN_PROGRESS &&
      step.template.type === 'user_validation'
    )
      this.approveWorkflowStepWithConfidentiality.emit();
    else this.approveWorkflowStepEvent.emit(step.id);
  }

  getWorkflowInstanceFormatedVersion(instance: WorkflowInstance) {
    if (instance.version_minor === 0) return `V${instance.version_major}`;
    return `V${instance.version_major}.${instance.version_minor}`;
  }

  workflowInstanceHasARejectedUserAction(instance: WorkflowInstance) {
    return instance.steps.some((step) =>
      step.user_actions.some(
        (action) => action.type === 'REJECT_DOCUMENT_VERIFICATION',
      ),
    );
  }

  getTimeFromDate(date: string) {
    return date.split('T')[1].split('.')[0];
  }

  getWorkflowStepTooltip(step: WorkflowStep): string {
    if (!step.user_actions.length) return '';
    return `by ${step.user_actions.map((action) => getUserIdentifier(action.user)).join(', ')}`;
  }

  getWorkflowOriginMessage(instance: WorkflowInstance): string {
    if (instance.template.type === WorkflowTemplateType.AUTOMATIC) {
      return 'Triggered';
    }

    return 'Requested';
  }

  getWorkflowOriginTooltip(instance: WorkflowInstance): string {
    if (instance.template.type === WorkflowTemplateType.AUTOMATIC) {
      return 'Triggered automatically';
    }

    return `by ${getUserIdentifier(instance.started_by)}`;
  }

  isStepRejected(step: WorkflowStep): boolean {
    return step.user_actions.some(
      (action) => action.type === 'REJECT_DOCUMENT_VERIFICATION',
    );
  }

  _getWorkflowStepStateClass(
    instance: WorkflowInstance,
    step: WorkflowStep,
  ): string {
    const workflowHasBeenRejected =
      this.workflowInstanceHasARejectedUserAction(instance);

    switch (step.state) {
      case WorkflowStepState.CANCELED:
        return 'workflow-rejected';
      case WorkflowStepState.COMPLETED:
        return 'workflow-approved';
      case WorkflowStepState.IN_PROGRESS:
        return 'workflow-pending';
      case WorkflowStepState.PENDING:
        return workflowHasBeenRejected
          ? 'workflow-skipped'
          : 'workflow-pending';
    }
  }

  _getWorkflowStepStateIcon(
    instance: WorkflowInstance,
    step: WorkflowStep,
  ): string {
    const workflowHasBeenRejected =
      this.workflowInstanceHasARejectedUserAction(instance);

    switch (step.state) {
      case WorkflowStepState.CANCELED:
        return this.isStepRejected(step) ? 'pi-ban' : 'pi-times';
      case WorkflowStepState.COMPLETED:
        return 'pi-check';
      case WorkflowStepState.IN_PROGRESS:
        return 'pi-sync';
      case WorkflowStepState.PENDING:
        return workflowHasBeenRejected ? 'pi-angle-double-right' : 'pi-clock';
    }
  }

  getWorkflowStepState(instance: WorkflowInstance, step: WorkflowStep) {
    return {
      class: this._getWorkflowStepStateClass(instance, step),
      icon: `pi ${this._getWorkflowStepStateIcon(instance, step)}`,
      value: step.template.name,
    };
  }

  _getWorkflowStateIcon(instance: WorkflowInstance): string {
    switch (instance.template.type) {
      case WorkflowTemplateType.USER_VALIDATION:
        switch (instance.state) {
          case WorkflowInstanceState.CANCELED:
            return this.workflowInstanceHasARejectedUserAction(instance)
              ? 'pi-ban'
              : 'pi-times';
          case WorkflowInstanceState.COMPLETED:
            return 'pi-verified';
          case WorkflowInstanceState.WAITING:
            return 'pi-sync';
          default:
            return this.DEFAULT_ICON_STATE;
        }
      case WorkflowTemplateType.DISTRIBUTION_CAMPAIGN:
        switch (instance.state) {
          case WorkflowInstanceState.CANCELED:
            return 'pi-ban';
          case WorkflowInstanceState.COMPLETED:
            return 'pi-book';
          case WorkflowInstanceState.WAITING:
            return 'pi-sync';
          default:
            return this.DEFAULT_ICON_STATE;
        }
      case WorkflowTemplateType.AUTOMATIC:
        switch (instance.state) {
          case WorkflowInstanceState.CANCELED:
            return 'pi-times';
          case WorkflowInstanceState.COMPLETED:
            return 'pi-cog';
          case WorkflowInstanceState.WAITING:
            return 'pi-sync';
          default:
            return this.DEFAULT_ICON_STATE;
        }
    }
  }

  _getWorkflowStateClass(instance: WorkflowInstance): string {
    switch (instance.state) {
      case WorkflowInstanceState.COMPLETED:
        return 'workflow-approved';
      case WorkflowInstanceState.CANCELED:
        return 'workflow-rejected';
      case WorkflowInstanceState.WAITING:
        return 'workflow-pending';
      default:
        return '';
    }
  }

  getWorkflowState(instance: WorkflowInstance) {
    return {
      class: this._getWorkflowStateClass(instance),
      icon: `pi ${this._getWorkflowStateIcon(instance)}`,
      value: instance.template.name,
    };
  }
}
